import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { CataloguePageHeader } from '@/components/catalogue/Header';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Property, SelectField } from '@/components/form/fields/select/SelectInput';
import { Form } from '@/components/form/Form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/form/Button';
import { Card } from '@/components/sections/Card';
import { useCreateLifeCyclePolicy, useLifeCyclePolicy, useUpdateLifeCyclePolicy } from '@/services/api/hooks/lifecycle';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';
import { CustomerCatalogueContainer } from '../CustomerCatalogueContainer';

const yesNoOptions: Property[] = [
  {
    id: 'yes',
    name: 'Yes'
  },
  {
    id: 'no',
    name: 'No'
  }
];

const redeploymentPhysicalConditionOptions = [
  {
    id: 'gradeA',
    name: 'Grade A'
  },
  {
    id: 'gradeB',
    name: 'Grade B'
  }
];

const redeploymentMaximumAgeOptions = [
  {
    id: '1',
    name: '1 Year'
  },
  {
    id: '1.5',
    name: '1.5 Years'
  },
  {
    id: '2',
    name: '2 Years'
  },
  {
    id: '2.5',
    name: '2.5 Years'
  },
  {
    id: '3',
    name: '3 Years'
  },
  {
    id: '3.5',
    name: '3.5 Years'
  },
  {
    id: '4',
    name: '4 Years'
  }
];

const LifecyclePolicyFields = z.object({
  newStarterEquipment: z.custom<Property>(),
  redeploymentMaxAge: z.custom<Property>(),
  redeploymentPhysicalCondition: z.custom<Property>(),
  techRefresh: z.custom<Property>()
});

type LifecyclePolicyFields = z.infer<typeof LifecyclePolicyFields>;

const LifecyclePoliciesContent: FunctionComponent = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Lifecycle Policies',
      to: ''
    }
  ];

  const { partnerId, companyId } = useParams();

  if (!(partnerId && companyId)) throw new Error('Msp or customer id not found');

  const [lifecyclePolicy] = useLifeCyclePolicy(partnerId, companyId);
  const createPolicy = useCreateLifeCyclePolicy(partnerId, companyId);
  const updatePolicy = useUpdateLifeCyclePolicy(partnerId, companyId);

  const defaultNewStarterEquipment = yesNoOptions.find(
    o => o.id === lifecyclePolicy?.policies.newStarterEquipmentPolicy
  );
  const defaultTechRefresh = yesNoOptions.find(o => o.id === lifecyclePolicy?.policies.techRefreshPolicy);
  const defaultRedeploymentMaximumAgeOptions = redeploymentMaximumAgeOptions.find(
    o => o.id === lifecyclePolicy?.policies.redeploymentPolicyMaxAge
  );
  const defaultRedeploymentPolicyPhysicalCondition = redeploymentPhysicalConditionOptions.find(
    o => o.id === lifecyclePolicy?.policies.redeploymentPolicyPhysicalCondition
  );

  const methods = useForm<LifecyclePolicyFields>({
    resolver: zodResolver(LifecyclePolicyFields),
    defaultValues: {
      newStarterEquipment: defaultNewStarterEquipment,
      techRefresh: defaultTechRefresh,
      redeploymentMaxAge: defaultRedeploymentMaximumAgeOptions,
      redeploymentPhysicalCondition: defaultRedeploymentPolicyPhysicalCondition
    }
  });

  const onSubmit = (data: LifecyclePolicyFields) => {
    const payload = {
      policies: {
        newStarterEquipmentPolicy: data.newStarterEquipment.id,
        techRefreshPolicy: data.techRefresh.id,
        redeploymentPolicyMaxAge: data.redeploymentMaxAge.id,
        redeploymentPolicyPhysicalCondition: data.redeploymentPhysicalCondition.id
      }
    };
    if (lifecyclePolicy) {
      updatePolicy.mutateAsync(payload);
    } else {
      createPolicy.mutateAsync(payload);
    }
  };

  const role = useRole();

  const readOnly = (role.type === RoleType.Company && lifecyclePolicy !== null) || role.type === RoleType.Partner;

  const getError = () => {
    const createErrorObj = createPolicy.error as Error | undefined;
    const updateErrorObj = updatePolicy.error as Error | undefined;

    return createErrorObj ? createErrorObj.message : updateErrorObj ? updateErrorObj.message : '';
  };

  const error = getError();

  return (
    <div className="flex h-full flex-1 flex-col bg-white bg-opacity-50">
      <CataloguePageHeader title="Lifecycle Policies" breadcrumbs={breadcrumbs}>
        <div className="flex gap-4">{/*<HelpButton />*/}</div>
      </CataloguePageHeader>
      <Form
        className="flex h-full w-full flex-col space-y-4 py-3 overflow-y-auto"
        methods={methods}
        onSubmit={onSubmit}
      >
        <div className="px-6 py-8  h-full">
          <Card className="p-5 space-y-10">
            <div className="space-y-2 w-fit">
              <p className="text-2xl font-bold">New Starter Equipment Policy</p>
              <SelectField
                id="newStarterEquipment"
                label="Can used devices be deployed to New Starters?"
                options={yesNoOptions}
                placeholder="Please Select"
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2 w-fit">
              <p className="text-2xl font-bold">Tech Refresh Policy</p>
              <SelectField
                id="techRefresh"
                label="Can used devices be deployed for Tech Refreshes?"
                options={yesNoOptions}
                placeholder="Please Select"
                disabled={readOnly}
              />
            </div>
            <div className="space-y-2 w-full">
              <p className="text-2xl font-bold">Redeployment Policy</p>
              <p className="text-lg">Select device conditions acceptable for redeployment</p>
              <p className="text-">Devices that don't meet these criteria will be liquidated</p>

              <div className="w-full flex flex-row space-x-8 items-center">
                <SelectField
                  id="redeploymentMaxAge"
                  label="Maximum Age"
                  className="flex-1"
                  options={redeploymentMaximumAgeOptions}
                  placeholder="Please Select"
                  disabled={readOnly}
                />
                <p className="pt-6 text-lg">and</p>
                <SelectField
                  id="redeploymentPhysicalCondition"
                  label="Physical Condition"
                  className="flex-1"
                  options={redeploymentPhysicalConditionOptions}
                  placeholder="Please Select"
                  disabled={readOnly}
                />
              </div>
            </div>

            {error && <p className="text-red-500">{error}</p>}
            <div className="flex justify-center pt-4">
              <Button
                loading={createPolicy.isLoading || updatePolicy.isLoading}
                className="w-fit"
                variant={ButtonVariant.Primary}
                type="submit"
                disabled={readOnly}
              >
                Confirm
              </Button>
            </div>
          </Card>
        </div>
      </Form>
    </div>
  );
};

export const LifecyclePolicies: FunctionComponent = () => {
  return (
    <CustomerCatalogueContainer>
      <LifecyclePoliciesContent />
    </CustomerCatalogueContainer>
  );
};
